import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import store from "./redux/store";
import App from "./App";
import csrfManager from "./redux/authentication/utils/core/utils/csrfMainManager";
import attachAxiosAuthInterceptors from "./redux/authentication/utils/core/utils/attachAPIClientAuthInterceptor";
import { logoutUser, clearAuth } from "./redux/authentication/utils/core/authslice";
import ErrorPage from "./pages/Error404/route404Page";
import StockerLoading from './components/loader/StockerLoadingAnimation'
import IdleSessionWarning from "./components/session/IdleSessionWarning";
import { ConnectivityProvider } from "./connectivity/ConnectivityContext";
import ConnectivityBanner from "./connectivity/ConnectivityBanner";

const CONFIG = {
  STARTUP_TIMEOUT: 2000,  // shorter because we no longer do heavy calls here
  RETRY_ATTEMPTS: 1,      // cold-start should not retry much
  RETRY_DELAY: 250,
};

function forceLogout(reason = "session_expired") {
  try {
    store.dispatch(logoutUser());
  } catch (error) {
    console.warn("[AppContainer] Logout dispatch failed:", error);
  }

  store.dispatch(clearAuth());
  const currentPath = window.location.pathname + window.location.search;

  if (window.location.pathname.startsWith("/login")) {
    window.location.replace(`/login?session=${reason}`);
    return;
  }

  const returnTo = encodeURIComponent(currentPath);
  window.location.replace(`/login?session=${reason}&returnTo=${returnTo}`);
}

async function withRetry(operation, maxRetries = CONFIG.RETRY_ATTEMPTS) {
  let lastError;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      console.warn(
        `[AppContainer] Attempt ${attempt + 1} failed:`,
        error.message
      );
      if (attempt < maxRetries - 1) {
        await new Promise((r) => setTimeout(r, CONFIG.RETRY_DELAY));
      }
    }
  }

  throw lastError;
}

const AppContainer = () => {
  const { t } = useTranslation();
  const [initializationError, setInitializationError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isInitializing, setIsInitializing] = useState(true);

  /**
   * Non-blocking cold-start init:
   * - attach interceptors (no network)
   * - if user is already authenticated, kick off CSRF init in background
   *   (do NOT await it here)
   */
  const checkServerStatus = async () => {
    // Setup interceptors FIRST (lightweight, no network calls)
    attachAxiosAuthInterceptors();

    const { auth } = store.getState();
    const isAuthed = !!auth?.isAuthenticated;

    // Only init CSRF if already logged in.
    // Don't block UI even then.
    if (isAuthed) {
      csrfManager.initialize().catch((e) => {
        console.warn("[AppContainer] CSRF init failed (non-fatal):", e?.message);
      });
    }

    //console.log("[AppContainer] Cold-start init complete");
  };

  const initializeApp = async () => {
    try {
      setInitializationError(null);

      await Promise.race([
        withRetry(checkServerStatus),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error("Initialization timeout")),
            CONFIG.STARTUP_TIMEOUT
          )
        ),
      ]);

      setRetryCount(0);
    } catch (error) {
      console.warn("[AppContainer] App initialization failed:", error);

      // Only show error for real network/server failures
      if (
        error.message?.includes("Network Error") ||
        error.message?.includes("timed out") ||
        error.message?.includes("Initialization timeout")
      ) {
        setInitializationError(
          "Unable to connect to server. Please check your connection and try again."
        );
      } else if (error?.response?.status >= 500) {
        setInitializationError(
          "Server is experiencing issues. Please try again in a few moments."
        );
      } else {
        // Non-fatal: continue
        console.warn("[AppContainer] Continuing despite init error");
      }
    } finally {
      setIsInitializing(false);
    }
  };

  const handleRetry = () => {
    setIsInitializing(true);
    setRetryCount((prev) => prev + 1);
    initializeApp();
  };

  useEffect(() => {
    initializeApp();
  }, []);

  // Global error handler
  useEffect(() => {
    const handleUnhandledError = (event) => {
      console.warn("[AppContainer] Unhandled error:", event.error);
    };

    const handleUnhandledRejection = (event) => {
      console.warn(
        "[AppContainer] Unhandled promise rejection:",
        event.reason
      );
    };

    window.addEventListener("error", handleUnhandledError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleUnhandledError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  return (
    <ConnectivityProvider>
      <Provider store={store}>
        <BrowserRouter>
          {initializationError ? (
            <ErrorPage
              title={t('errorPage.connectionFailedTitle')}
              description={initializationError}
              onRetry={handleRetry}
            />
          ) : (
            <>
              <App />

              {/* 15-min-idle auto-logout + active-session keep-alive; renders
                  nothing unless a warning is actively counting down. */}
              <IdleSessionWarning />

              {/* Persistent shell-level online/offline/slow indicator --
                  extends this file's own cold-start health-check race into
                  an ongoing check for the rest of the session. Renders
                  nothing while connectivity is healthy. */}
              <ConnectivityBanner />

              {/* Full-screen Stocker loader overlay */}
              {isInitializing && (
                <StockerLoading retryCount={retryCount} />
              )}
            </>
          )}
        </BrowserRouter>
      </Provider>
    </ConnectivityProvider>
  );
};

export default AppContainer;
